import { z } from 'zod'

import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'
import type { PortalKind } from '@/lib/auth/token-storage'
export { portalDefaultPath as portalRedirectPath } from '@/lib/auth/portal-routes'

/**
 * 新前端的登录 API 适配层。
 * 接口路径和响应结构集中在这里，避免界面层泄漏旧前端命名或 token 存储细节。
 */
export const authCredentialsSchema = z.object({
  email: z.email(),
  password: z.string().trim().min(1),
})

export type AuthCredentials = z.infer<typeof authCredentialsSchema>

/**
 * 登录成功后页面层真正会使用的账号字段。
 * 额外字段保留在后端 payload 中，不在登录 UI contract 暴露。
 */
export interface AuthenticatedAccount {
  email: string
  id: string
  name: string
}

interface LoginSuccessResponse {
  code: 'success'
  data: {
    password_reset_required?: boolean
    refresh_token?: string | null
    token: string
    user: AuthenticatedAccount
  }
}

interface LoginFailResponse {
  code: 'fail'
  data: string
  message?: string
}

export type LoginResponse = LoginSuccessResponse | LoginFailResponse

const portalLoginPath: Record<PortalKind, string> = {
  admin: '/admin/auth/login',
  user: '/app/auth/login',
}

const userRecoveryLoginPath = '/app/auth/login/password'

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
})

/**
 * 按门户登录，并保留旧前端的接口划分：
 * 前台使用 `/app/auth/login`，后台使用 `/admin/auth/login`。
 */
export function loginToPortal(
  portal: PortalKind,
  credentials: AuthCredentials,
  options?: { recovery?: boolean },
) {
  const path =
    portal === 'user' && options?.recovery ? userRecoveryLoginPath : portalLoginPath[portal]
  return apiClient.request<LoginResponse>(path, {
    body: JSON.stringify(credentials),
    credentials: portal === 'user' ? 'include' : undefined,
    method: 'POST',
  })
}
